import React from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  placeholder?: string
  disabled?: boolean
  isMobile?: boolean
  onSnippetsClick?: () => void
  agentCode?: string
  readOnly?: boolean
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = 'Escreva sua mensagem...',
  disabled = false,
  isMobile = false,
  onSnippetsClick,
  agentCode,
  readOnly = false,
}) => {
  // Show read-only message when user has view-only access
  const effectivePlaceholder = readOnly
    ? 'Você tem apenas permissão de visualização nesta conversa'
    : placeholder
  const canSend = value.trim().length > 0

  const handleSend = () => {
    if (!canSend) return
    Promise.resolve(onSend()).catch(() => {
      // Silent fail - error handling done in parent
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value)
  }

  if (isMobile) {
    return (
      <div
        data-testid="chat-input-mobile-container"
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 elegant-divider-top p-4 z-40"
      >
        <div className="relative">
          <textarea
            data-testid="chat-input-mobile"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={effectivePlaceholder}
            className={`w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white chat-textarea-dark dark:text-white text-sm min-h-[60px] shadow-sm ${onSnippetsClick && agentCode ? 'pr-24' : 'pr-12'}`}
            rows={2}
            disabled={disabled}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            {/* Shortcuts Icon Button */}
            {onSnippetsClick && agentCode && (
              <IconButton
                onClick={onSnippetsClick}
                disabled={disabled}
                variant="brand"
                size="sm"
                shape="square"
                data-testid="open-snippets-mobile"
                aria-label="Atalhos Rápidos"
              >
                <Icon name="bolt" size={20} />
              </IconButton>
            )}
            {/* Send Button */}
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              variant="dark"
              size="sm"
              shape="square"
              aria-label="Enviar mensagem"
            >
              <Icon name="send" size={16} />
            </IconButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="chat-input-desktop-container"
      className="hidden lg:block flex-shrink-0 px-4 lg:px-6 py-4 elegant-divider-top"
    >
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          <textarea
            data-testid="chat-input-desktop"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={effectivePlaceholder}
            className={`w-full px-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white chat-textarea-dark dark:text-white text-sm min-h-[100px] shadow-sm ${onSnippetsClick && agentCode ? 'pr-28' : 'pr-16'}`}
            rows={4}
            disabled={disabled}
          />
          <div className="absolute right-3 bottom-3 flex items-center gap-1.5">
            {/* Shortcuts Icon Button */}
            {onSnippetsClick && agentCode && (
              <div className="relative group">
                <IconButton
                  onClick={onSnippetsClick}
                  disabled={disabled}
                  variant="brand"
                  size="sm"
                  shape="square"
                  data-testid="open-snippets-desktop"
                  aria-label="Atalhos Rápidos"
                >
                  <Icon name="bolt" size={20} />
                </IconButton>
                {/* Tooltip */}
                <span className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Atalhos Rápidos
                </span>
              </div>
            )}
            {/* Send Button */}
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              variant="dark"
              size="sm"
              shape="square"
              aria-label="Enviar mensagem"
            >
              <Icon name="send" size={16} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )
}
